import { formatDate } from "../../../utils/date";

export const CATALOG_PAGE_SIZE = 12;
export const MAX_CATALOG_FILES = 10;
export const MAX_FILE_SIZE_MB = 10;
export const ACCEPTED_MEDIA_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "video/mp4",
  "video/quicktime",
];
export const ACCEPT_ATTR = ACCEPTED_MEDIA_TYPES.join(",");

const ASSET_BASE = (process.env.REACT_APP_ASSET_URL || "https://images.allhomedesire.com").replace(/\/+$/, "");

/** Text for display: trimmed, and never "null" / "undefined". Empty string means "hide this row". */
export const cleanText = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  return /^(null|undefined)$/i.test(text) ? "" : text;
};

export const resolveMediaUrl = (path) => {
  const clean = cleanText(path);
  if (!clean) return "";
  if (/^(https?:|blob:|data:)/i.test(clean)) return clean;
  return `${ASSET_BASE}${clean.startsWith("/") ? "" : "/"}${clean}`;
};

export const isVideoUrl = (url) => /\.(mp4|mov|m4v)(\?|$)/i.test(cleanText(url));

/** Image/video entries of a catalog, with usable URLs only. */
export const getCatalogMedia = (catalog) =>
  (catalog?.catalog_images || [])
    .map((item) => resolveMediaUrl(item?.image_url || item?.imageUrl))
    .filter(Boolean)
    .map((url) => ({ url, isVideo: isVideoUrl(url) }));

export const getCatalogPrice = (catalog) => cleanText(catalog?.price);

/** Created date through the shared safe helper; "" when missing or invalid. */
export const getCatalogDate = (catalog) =>
  formatDate(catalog?.created_on ?? catalog?.createdOn ?? catalog?.updated_on, { fallback: "" });

export const getWebsiteHref = (url) => {
  const clean = cleanText(url);
  if (!clean) return "";
  return /^https?:\/\//i.test(clean) ? clean : `https://${clean}`;
};

/** "1,250 INR" -> "1250" for the edit form. */
export const priceToInput = (price) => {
  const match = cleanText(price).replace(/,/g, "").match(/\d+(\.\d+)?/);
  return match ? String(Math.round(Number(match[0]))) : "";
};

/** Same stored format as the mobile app: "1,250 INR". */
export const formatPriceForApi = (digits) => `${Number(digits).toLocaleString("en-IN")} INR`;

/**
 * Edit / Delete are only offered on catalogs owned by the logged-in partner.
 * `assumeOwn` is for lists already scoped to the partner (mySpace) where the owner is not echoed back.
 */
export const isCatalogOwner = (catalog, userId, assumeOwn = false) => {
  const ownerId = catalog?.partner?.id ?? catalog?.partner_id;
  if (ownerId === undefined || ownerId === null) return assumeOwn;
  return userId !== undefined && userId !== null && String(ownerId) === String(userId);
};

/** Returns an error message for a file the form should not accept, or "". */
export const validateMediaFile = (file) => {
  if (!ACCEPTED_MEDIA_TYPES.includes(file.type)) {
    return `"${file.name}" is not a supported type. Use JPG, PNG, WebP, GIF, MP4 or MOV.`;
  }
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return `"${file.name}" is larger than ${MAX_FILE_SIZE_MB} MB.`;
  }
  return "";
};

export const validateCatalogForm = ({ typeId, price, description, websiteUrl, fileCount, isEdit }) => {
  const errors = {};
  if (!typeId) errors.typeId = "Select a category.";
  if (!price || Number(price) <= 0) errors.price = "Enter a valid price.";
  if (!cleanText(description)) errors.description = "Add a short description.";
  if (cleanText(websiteUrl) && !/^(https?:\/\/)?[^\s.]+\.[^\s]{2,}$/i.test(cleanText(websiteUrl))) {
    errors.websiteUrl = "Enter a valid website address.";
  }
  if (!isEdit && fileCount === 0) errors.media = "Add at least one image or video.";
  return errors;
};
